import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  readActiveAgreementsRequest,
  saveConsentRequest,
} from "../../store/Agreements/agreementsActions";
import { getProfileAction } from "../../store/Profile/ProfileAction";
import { updateUserProfileInfoAction } from "../../store/Users/UsersAction";
import { pickPreferredCmsProfile } from "../../utils/portalAccess";

const AgreementConsent = ({ onComplete }) => {
  const dispatch = useDispatch();
  const { activeAgreements, loading } = useSelector((state) => state.agreements || {});
  const { userInfo } = useSelector((state) => state.user || {});

  const [entityType, setEntityType] = useState("1");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);

  const resolvedEntityType = useMemo(() => {
    if (userInfo?.roleName === "Ministry Admin") return "3";
    if (userInfo?.roleName === "Channel Admin") return "2";
    if (userInfo?.roleName === "Church Admin") return "1";

    const urls = (userInfo?.portalUrls || []).map((u) => String(u || "").toLowerCase());
    if (urls.some((u) => u.includes("ministries.todaywithgod.org"))) return "3";
    if (urls.some((u) => u.includes("channels.todaywithgod.org"))) return "2";
    if (urls.some((u) => u.includes("churches.todaywithgod.org"))) return "1";
    return entityType;
  }, [userInfo?.roleName, userInfo?.portalUrls, entityType]);

  const pendingAgreementIds = useMemo(() => {
    const statuses = (userInfo?.agreementStatuses || []).filter(
      (s) => String(s?.typeofentity) === String(resolvedEntityType)
    );
    const agreed = new Set();
    const pending = new Set();
    statuses.forEach((s) => {
      const id =
        s?.agreementObjId ||
        s?.agreementType ||
        s?.agreementTitle ||
        "";
      if (!id) return;
      if (String(s?.status) === "1" && s?.requiresReaccept !== true) {
        agreed.add(id);
      } else {
        pending.add(id);
      }
    });
    return { agreed, pending };
  }, [userInfo?.agreementStatuses, resolvedEntityType]);

  useEffect(() => {
    setEntityType(resolvedEntityType);
    dispatch(readActiveAgreementsRequest({ typeofentity: resolvedEntityType }, () => {}));
    setHasRequested(true);
  }, [dispatch, resolvedEntityType]);

  useEffect(() => {
    if (!activeAgreements || activeAgreements.length === 0) {
      return;
    }
    setCurrentIndex(0);
  }, [activeAgreements]);

  const agreementTextMap = useMemo(() => {
    const map = {};
    (activeAgreements || []).forEach((a) => {
      const key = a?._id || a?.agreementObjId || a?.agreementType;
      if (key) {
        map[key] = {
          agreementObjId: a?._id || a?.agreementObjId || key,
          agreementText: a?.agreementText || "",
          agreementTitle: a?.agreementTitle || a?.agreementType || "",
          updatedAt: a?.versionUpdatedAt || a?.lastUpdatedDate || a?.createdDate || null,
        };
      }
    });
    return map;
  }, [activeAgreements]);

  const filteredAgreements = useMemo(() => {
    if (!Array.isArray(activeAgreements) || activeAgreements.length === 0) return [];
    const results = [];
    activeAgreements.forEach((agreement) => {
      const id = agreement?._id || agreement?.agreementObjId || agreement?.agreementType;
      if (!id) return;
      const isAgreed = pendingAgreementIds.agreed.has(id);
      const needsAccept = pendingAgreementIds.pending.has(id) || !isAgreed;
      if (!needsAccept) return;
      const meta = agreementTextMap[id] || {};
      results.push({
        agreementObjId: meta?.agreementObjId || agreement?._id || id,
        agreementTitle: meta?.agreementTitle || agreement?.agreementTitle || agreement?.agreementType || "Agreement",
        agreementUpdatedAt: meta?.updatedAt || null,
        agreementText:
          meta?.agreementText ||
          agreement?.agreementText ||
          "Agreement text is not available right now. Please accept to proceed.",
      });
    });
    return results;
  }, [activeAgreements, agreementTextMap, pendingAgreementIds]);

  useEffect(() => {
    if (!hasRequested) return;
    if (loading) return;
    if (!activeAgreements || activeAgreements.length === 0) {
      if (typeof onComplete === "function") onComplete();
      return;
    }
    if (filteredAgreements.length === 0) {
      if (typeof onComplete === "function") onComplete();
    }
  }, [hasRequested, loading, activeAgreements, filteredAgreements.length, onComplete]);

  const currentAgreement = useMemo(
    () => (filteredAgreements || [])[currentIndex],
    [filteredAgreements, currentIndex]
  );

  const formatUpdatedDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = String(date.getDate()).padStart(2, "0");
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleAccept = () => {
    if (!currentAgreement?.agreementObjId) return;
    if (isSaving) return;
    setIsSaving(true);
    dispatch(
      saveConsentRequest(
        {
          typeofentity: resolvedEntityType,
          agreementObjId: currentAgreement.agreementObjId,
          status: "1",
        },
        (res) => {
          setIsSaving(false);
          if (res?.type === 1) {
            const nextIndex = currentIndex + 1;
            if (nextIndex < (filteredAgreements || []).length) {
              setCurrentIndex((prev) => prev + 1);
              return;
            }
            dispatch(
              getProfileAction({
                apiPayloadRequest: {},
                callback: (profileRes) => {
                  if (
                    profileRes?.type === 1 &&
                    Array.isArray(profileRes?.data) &&
                    profileRes.data.length > 0
                  ) {
                    const selectedProfile = pickPreferredCmsProfile(profileRes.data) || profileRes.data[0];
                    dispatch(updateUserProfileInfoAction(selectedProfile));
                  }
                  if (typeof onComplete === "function") onComplete();
                },
              })
            );
          } else {
            toast.error(res?.error || "Failed to save consent");
          }
        }
      )
    );
  };

  const handleDecline = () => {
    toast.error("Please agree to continue.");
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center">
        <div className="text-yellowish text-sm">Loading agreements...</div>
      </div>
    );
  }

  if (!currentAgreement) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center px-4">
      <div className="w-full max-w-2xl bg-[#121212] rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden border border-[#2b2b2b]">
        <div className="px-6 py-4 border-b border-[#2b2b2b] flex items-center justify-center text-center">
          <div>
            <p className="text-[18px] font-semibold text-yellowish">
              Terms and Conditions
            </p>
            <p className="text-[20px] font-semibold text-white mt-2">
              {currentAgreement?.agreementTitle || currentAgreement?.agreementType}
            </p>
            {currentAgreement?.agreementUpdatedAt && (
              <p className="text-[11px] text-greyish mt-1">
                Agreement updated on {formatUpdatedDate(currentAgreement.agreementUpdatedAt)}
              </p>
            )}
          </div>
        </div>

        <div className="px-6 py-4">
          <div
            className="agreement-richtext h-[45vh] overflow-y-auto rounded-xl border border-[#2b2b2b] bg-[#0f0f0f] p-4 text-[13px] text-white leading-6"
            dangerouslySetInnerHTML={{
              __html: currentAgreement?.agreementText || "No content provided.",
            }}
          />
        </div>

        <div className="px-6 py-4 border-t border-[#2b2b2b] flex items-center justify-between">
          <p className="text-[12px] text-greyish">
            Agreement {currentIndex + 1} of {(filteredAgreements || []).length}
          </p>
          <div className="flex gap-3">
            <button
              className="px-6 py-2 rounded-lg border border-[#3a3a3a] text-[#b0b0b0] text-sm"
              onClick={handleDecline}
              disabled={isSaving}
            >
              Decline
            </button>
            <button
              className="px-6 py-2 rounded-lg bg-yellowish text-black text-sm shadow"
              onClick={handleAccept}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Accept"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementConsent;
